import { CopyButton } from '@/components/copy-button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

interface Snippet {
  /** Where the snippet goes, when a platform needs more than one. */
  label?: string;
  code: string;
}

interface Platform {
  value: string;
  label: string;
  snippets: (dsn: string) => Snippet[];
}

const PLATFORMS: Platform[] = [
  { value: 'browser', label: 'Browser', snippets: (dsn) => [{ code: browserSnippet(dsn) }] },
  { value: 'react', label: 'React', snippets: (dsn) => [{ code: reactSnippet(dsn) }] },
  { value: 'node', label: 'Node', snippets: (dsn) => [{ code: nodeSnippet(dsn) }] },
  { value: 'rust', label: 'Rust', snippets: rustSnippets },
  { value: 'go', label: 'Go', snippets: goSnippets },
];

/** The platform an event reports, mapped to the tab that set it up. */
const TAB_FOR_PLATFORM: Record<string, string> = {
  javascript: 'browser',
  node: 'node',
  rust: 'rust',
  go: 'go',
};

/**
 * Setup instructions for every SDK, filled in with the project's DSN.
 *
 * Opens on the project's first known platform, so a project created as a Go
 * service does not start by showing a browser snippet.
 */
export function SdkInstall({ dsn, platforms = [] }: { dsn: string; platforms?: string[] }) {
  const initial =
    platforms.map((platform) => TAB_FOR_PLATFORM[platform]).find(Boolean) ?? 'browser';

  return (
    // Keyed so the tab follows the platforms if they load after the first render.
    <Tabs key={initial} defaultValue={initial}>
      <TabsList>
        {PLATFORMS.map((platform) => (
          <TabsTrigger key={platform.value} value={platform.value}>
            {platform.label}
          </TabsTrigger>
        ))}
      </TabsList>

      {PLATFORMS.map((platform) => (
        <TabsContent key={platform.value} value={platform.value} className="space-y-3">
          {platform.snippets(dsn).map((snippet) => (
            <div key={snippet.label ?? platform.value} className="space-y-1.5">
              {snippet.label && (
                <p className="font-mono text-xs text-muted-foreground">{snippet.label}</p>
              )}
              <CodeBlock code={snippet.code} />
            </div>
          ))}
        </TabsContent>
      ))}
    </Tabs>
  );
}

function CodeBlock({ code }: { code: string }) {
  return (
    <div className="relative">
      <pre className="scrollbar-thin overflow-x-auto rounded-md bg-muted p-4 text-xs leading-relaxed">
        <code>{code}</code>
      </pre>
      <div className="absolute right-2 top-2">
        <CopyButton value={code} />
      </div>
    </div>
  );
}

const placeholder = (dsn: string) => dsn || 'https://<public-key>@<host>/<project-id>';

/**
 * The Rust and Go SDKs are installed from a git tag, and every Outpost release
 * tags them with its own version — so the SDK that matches this server is the
 * one tagged with the dashboard's version. A local build has no release to
 * point at, and falls back to main.
 */
const RELEASE_TAG = /^\d/.test(__APP_VERSION__) ? `v${__APP_VERSION__}` : null;

/**
 * From v2 on, Go needs a /v2 suffix on the module path, which the SDK does not
 * have yet, so releases stop tagging it and `go get` has to follow main.
 */
const GO_REF = RELEASE_TAG && Number.parseInt(__APP_VERSION__, 10) < 2 ? RELEASE_TAG : 'main';

function browserSnippet(dsn: string): string {
  return `import * as Outpost from '@peaky-ai/outpost-browser';

Outpost.init({
  dsn: '${placeholder(dsn)}',
  environment: 'production',
  release: '1.0.0',
});`;
}

function reactSnippet(dsn: string): string {
  return `import * as Outpost from '@peaky-ai/outpost-browser';
import { ErrorBoundary } from '@peaky-ai/outpost-browser/react';

Outpost.init({ dsn: '${placeholder(dsn)}', environment: 'production' });

export function App() {
  return (
    <ErrorBoundary fallback={(error, reset) => (
      <div>
        <p>{error.message}</p>
        <button onClick={reset}>Try again</button>
      </div>
    )}>
      <Routes />
    </ErrorBoundary>
  );
}`;
}

function nodeSnippet(dsn: string): string {
  return `import * as Outpost from '@peaky-ai/outpost-node';

Outpost.init({
  dsn: '${placeholder(dsn)}',
  environment: process.env.NODE_ENV,
  release: process.env.GIT_SHA,
});`;
}

function rustSnippets(dsn: string): Snippet[] {
  const ref = RELEASE_TAG ? `tag = "${RELEASE_TAG}"` : 'branch = "main"';
  return [
    {
      label: 'Cargo.toml',
      code: `[dependencies]
outpost = { package = "outpost-sdk", git = "https://github.com/peaky-ai/peaky-outpost", ${ref} }`,
    },
    {
      label: 'src/main.rs',
      code: `fn main() {
    // Keep the guard alive: dropping it flushes queued events and stops reporting.
    let _outpost = outpost::init(outpost::Options {
        dsn: Some("${placeholder(dsn)}".into()),
        environment: Some("production".into()),
        release: Some(env!("CARGO_PKG_VERSION").into()),
        ..Default::default()
    })
    .expect("a valid Outpost DSN");

    if let Err(error) = run() {
        outpost::capture_error(&error);
    }
}`,
    },
  ];
}

function goSnippets(dsn: string): Snippet[] {
  return [
    {
      label: 'Terminal',
      code: `go env -w GOPRIVATE=github.com/peaky-ai/*
go get github.com/peaky-ai/peaky-outpost/sdks/go@${GO_REF}`,
    },
    {
      label: 'main.go',
      code: `import outpost "github.com/peaky-ai/peaky-outpost/sdks/go"

func main() {
	err := outpost.Init(outpost.Options{
		DSN:         "${placeholder(dsn)}",
		Environment: "production",
		Release:     version,
	})
	if err != nil {
		log.Fatal(err)
	}
	defer outpost.Close(2 * time.Second)
	defer outpost.Recover()

	if err := run(); err != nil {
		outpost.CaptureError(err, nil)
	}
}`,
    },
  ];
}
